import { ReparoApi } from './index';

/** DoH/MoH rate basis: BACKWARD = past-sales DRR, FORWARD = forecast. */
export type RateBasis = 'FORWARD' | 'BACKWARD';

export type WarehouseStockPlanningResponse = {
    data: {
        product_id: string;
        product_name: string;
        sku: string;
        plan: {
            opening_stock: number;
            consumption: number;
            doh: number | null;
            moh?: number | null;
            in_transit: number;
            in_production?: number;
            /** FIRM-mother remainder — an external party is making these against a PO. */
            in_production_po?: number;
            /** Open work-order production (batch open, or the EXTERNAL_QTY snapshot). */
            in_production_wo?: number;
            /** in_transit + in_production_po + in_production_wo, all on the arrival axis. */
            incoming?: number;
            label: string;
        }[];
    }[];
    total: number;
    page: number;
    pageSize: number;
    /**
     * Per-period totals over every SKU the filters matched, computed server-side before the
     * page was cut. Present on every response; the grid uses it whenever it holds only a page.
     */
    aggregate_row?: WarehouseStockPlanningAggregateRow;
    inventory_config?: {
        inventory_period: 'DAY' | 'MONTH';
        over_stock_limit: number;
        under_stock_limit: number;
        basis?: RateBasis;
        sales_lookback_days?: number;
    };
};

export type WarehouseStockPlanningAggregateRow = {
    /** SKUs the totals cover — the whole filtered set, not the page. */
    product_count: number;
    plan: {
        label: string;
        opening_stock: number;
        consumption: number;
        in_transit: number;
        in_production: number;
        incoming: number;
    }[];
};

export type WarehouseStockResponse = {
    data: {
        product_id: string;
        product_name: string;
        sku: string;
        quantity: number;
        on_hold_stock?: number | null;
        /**
         * Channel scope only: this SKU's total in-transit across the channel's warehouses.
         * Null for INTERNAL / OVERALL. Never part of `quantity` or `days_on_hand`.
         */
        in_transit?: number | null;
        /**
         * Channel scope, split channels only: the planned-not-dispatched part of what is
         * committed, disjoint from `in_transit` (which then holds the dispatched remainder).
         * Null when the channel's source cannot split the figure — see `scheduled_buckets`.
         */
        scheduled?: number | null;
        /**
         * Channel scope only: this SKU's units moving between the channel's OWN warehouses —
         * the opposite direction to `in_transit`, which is stock arriving from the brand.
         * Null when the channel does not report it. Never part of `quantity` or `days_on_hand`.
         */
        warehouse_transfer?: number | null;
        consumption_per_day?: number;
        consumption_per_month?: number;
        days_on_hand?: number;
        months_on_hand?: number;
        category?: string | null;
        /** Only present when itemType = 'material'. Values: 'RM' | 'PM'. */
        material_type?: string | null;
        /** Unit of measurement. Only present when itemType = 'material'. */
        unit?: string | null;
        groupings?: Record<string, {
            quantity: number;
            on_hold_stock?: number | null;
            /** Channel scope only: units inbound to this warehouse, not yet received. */
            in_transit?: number | null;
            /** Split channels only: the planned-not-dispatched part, disjoint from `in_transit`. */
            scheduled?: number | null;
            /** Channel scope only: units that have LEFT this warehouse for a sibling warehouse. */
            warehouse_transfer?: number | null;
            warehouse_name?: string;
            /** Only populated for material queries. Values: 'WAREHOUSE' | 'VENDOR'. */
            facility_type?: string | null;
            /** Only populated when facility_type='VENDOR' (e.g. 'Contract Manufacturer'). */
            vendor_role?: string | null;
            /**
             * The synthetic Stock Transfer location: units between two of the brand's own
             * warehouses, dispatched and not yet received. Part of `quantity` like any location.
             */
            is_stock_transfer?: boolean;
            /** One of the brand's material stores: the Snapshot shows it after the other warehouses. */
            is_material_store?: boolean;
        }>;
    }[];
    total: number;
    page: number;
    pageSize: number;
    inventory_config?: {
        inventory_period: 'DAY' | 'MONTH';
        over_stock_limit: number;
        under_stock_limit: number;
        basis?: RateBasis;
        sales_lookback_days?: number;
    };
    blended_days_on_hand?: number;
    total_quantity?: number;
    total_in_transit?: number | null;
    /** PO vs STN split of in-transit (sum to total_in_transit). */
    total_in_transit_via_po?: number | null;
    total_in_transit_via_stn?: number | null;
    total_in_production?: number | null;
    total_incoming?: number | null;
    /**
     * Same contract as the DoH view's field of the same name: `'overall'` for the Total
     * Inventory column, plus one grouping key per channel warehouse. Empty outside a
     * channel scope.
     */
    in_transit_buckets?: string[];
    /**
     * Subset of `in_transit_buckets` whose reading is split into `scheduled` + `in_transit`,
     * so the grid renders a Scheduled column beside In Transit for exactly these.
     */
    scheduled_buckets?: string[];
    /**
     * Grouping keys that carry a warehouse-transfer reading, same contract as
     * `in_transit_buckets`. Empty unless the channel reports the figure — that is the
     * per-merchant release gate, so no column beats a column of blanks.
     */
    warehouse_transfer_buckets?: string[];
    /**
     * The Stock Transfer location's grouping key when the brand's rows carry one (INTERNAL /
     * OVERALL, brands with 2+ active internal warehouses and an open or recent transfer).
     */
    stock_transfer_buckets?: string[];
    low_stock_count?: number;
    over_stock_count?: number;
    aggregate_row?: {
        quantity: number;
        on_hold_stock?: number | null;
        in_transit?: number | null;
        scheduled?: number | null;
        warehouse_transfer?: number | null;
        consumption_per_day?: number;
        consumption_per_month?: number;
        days_on_hand?: number;
        months_on_hand?: number;
        groupings: Record<
            string,
            {
                quantity: number;
                on_hold_stock?: number | null;
                in_transit?: number | null;
                scheduled?: number | null;
                warehouse_transfer?: number | null;
            }
        >;
    };
};

export type SortBy = 'DEFAULT' | 'TOP_SELLING' | 'LOW_STOCK' | 'OVER_STOCK';

type WarehouseStockPayload = {
    categories?: string[];
    productGroups?: string[];
    tiers?: string[];
    productIds?: string[];
    isActive?: boolean;
    type?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: SortBy;
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
    groupBy?: 'warehouse' | 'city' | 'state';
    /** Filter by item type. Backend defaults to 'product' when omitted. */
    itemType?: 'product' | 'material';
    /** RM/PM filter — only meaningful when itemType = 'material'. */
    materialType?: 'RM' | 'PM' | 'STAGE';
    /** DoH/MoH rate basis override. Omit to use the org's saved config. */
    basis?: RateBasis;
    /** DRR lookback window in days (only used when basis = BACKWARD). */
    drrWindow?: number;
};

export const getWarehouseStock = async (
    payload: WarehouseStockPayload = {},
): Promise<WarehouseStockResponse> => {
    const body = { ...payload };
    if (!body.search) {
        delete body.search;
    }
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock',
        body: JSON.stringify(body),
    });
};

/**
 * Returns the total count of active INTERNAL warehouses for the current merchant.
 * Used by the sidebar to conditionally show the Rebalance menu item.
 */
export const getInternalWarehouseCount = async (): Promise<number> => {
    const res = await ReparoApi<{ total: number }>({
        method: 'POST',
        url: '/warehouses/list',
        body: JSON.stringify({ types: ['INTERNAL'], isActive: true, pageSize: 1 }),
    });
    return res.total;
};

export type InTransitDetailItem = {
    expectedDate?: string;
    benchmarkDate?: string;
    /** Instrument reference — PO id or STN code. */
    purchaseOrderId: string;
    /** Instrument type: 'PO' | 'STN' | 'WO'. */
    source?: string;
    /** Which supply classification: 'IN_TRANSIT' (moving) or 'IN_PRODUCTION' (being made).
     *  The Incoming column sums both, so the modal lists both. */
    state?: string;
    status: string;
    quantity: number;
};

export type InTransitDetailResponse = {
    data: InTransitDetailItem[];
};

export const getInTransitDetail = async (payload: {
    productId: string;
    periodStart: string;
    periodEnd: string;
}): Promise<InTransitDetailResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/in-transit-detail',
        body: JSON.stringify(payload),
    });
};

export const getWarehouseStockPlanning = async ({
    categories,
    productGroups,
    tiers,
    productIds,
    isActive,
    page,
    pageSize,
    sortBy,
    search,
    itemType,
    type,
    basis,
    drrWindow,
}: {
    categories?: string[];
    productGroups?: string[];
    tiers?: string[];
    productIds?: string[];
    isActive?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: SortBy;
    search?: string;
    /** Filter by item type. Backend defaults to 'product' when omitted. */
    itemType?: 'product' | 'material';
    type?: string;
    /** DoH/MoH rate basis override. Omit to use the org's saved config. */
    basis?: RateBasis;
    /** DRR lookback window in days (only used when basis = BACKWARD). */
    drrWindow?: number;
} = {}): Promise<WarehouseStockPlanningResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/planning',
        body: JSON.stringify({
            categories: categories?.length ? categories : undefined,
            productGroups: productGroups?.length ? productGroups : undefined,
            tiers: tiers?.length ? tiers : undefined,
            productIds: productIds?.length ? productIds : undefined,
            isActive,
            page,
            pageSize,
            sortBy,
            search: search || undefined,
            itemType,
            type,
            basis,
            drrWindow,
        }),
    });
};

// ── DRR View (inventory-drr-view-v1) ────────────────────────────────────────

export type DrrBucket = {
    drr: number;
    doh: number | null;
    stock: number | null;
    /**
     * Channel pivot only (mode='nodes' on a real channel): units committed to this node /
     * channel and not yet received. Null or absent everywhere else. Never part of `stock`,
     * and therefore never part of `doh` — it gets its own column beside Inv.
     */
    in_transit?: number | null;
    /**
     * Channel pivot, split channels only: the planned-not-dispatched part of what is committed
     * to this node, disjoint from `in_transit` (which then holds the dispatched remainder);
     * the two sum to the single figure other channels show. See `scheduled_buckets`.
     */
    scheduled?: number | null;
    /**
     * Channel pivot only: units that have LEFT this node for a sibling node of the same
     * channel and not yet landed. Opposite direction to `in_transit` and disjoint from it —
     * that one is stock arriving from the brand, this is the channel moving its own stock.
     * Null or absent when the channel does not report it. Never part of `stock` or `doh`.
     */
    warehouse_transfer?: number | null;
    /**
     * Internal buckets only: units between two of the brand's own warehouses, dispatched and
     * not yet received, at the destination. INCLUDED in `stock` and `doh`; Inv renders
     * `stock − stock_transfer` and this gets its own column. See `stock_transfer_buckets`.
     */
    stock_transfer?: number | null;
};

export type DrrViewRow = {
    product_id: string;
    sku: string;
    name: string;
    category?: string | null;
    tier?: string | null;
    product_group?: string | null;
    is_active?: boolean | null;
    overall: DrrBucket;
    internal: DrrBucket;
    channels: Record<string, DrrBucket>;
    /** Node mode, OR channels only: sell-out totals across the channel's nodes. */
    channel_secondary?: DrrBucket;
    /**
     * Present only on rollup rows: the member SKU rows, rendered as expandable
     * sub-rows. A rollup row is otherwise identical in shape to a SKU row.
     */
    subRows?: DrrViewRow[];
    /** Present only on rollup rows: how many SKUs the row aggregates. */
    skuCount?: number;
};

export type DrrViewResponse = {
    data: DrrViewRow[];
    channels: Array<{ key: string; node_backed: boolean }>;
    mode: 'channels' | 'nodes';
    /** Node mode: pivoted channel is B2B-OR (channel total = PO sell-in). */
    channel_is_or?: boolean;
    /**
     * Node mode: rows carry business-wide Internal figures beside the pivoted channel
     * (per-brand opt-in). Absent means row.internal is unset — do not render the group.
     */
    internal_included?: boolean;
    /**
     * Bucket keys that carry an in-transit reading, so the grid renders an In Transit column
     * for exactly these and nothing else. `'overall'` is the row's total column; the rest are
     * node keys. `internal` and `Unattributed` never appear.
     *
     * Declares scope, not data presence — a node with nothing inbound is still listed, so the
     * columns don't appear and vanish while paging.
     */
    in_transit_buckets?: string[];
    /**
     * Subset of `in_transit_buckets` whose reading is split into `scheduled` + `in_transit`,
     * so the grid renders a Scheduled column beside In Transit for exactly these.
     */
    scheduled_buckets?: string[];
    /**
     * Bucket keys that carry a warehouse-transfer reading, same contract as
     * `in_transit_buckets`. Empty unless the pivoted channel reports the figure.
     */
    warehouse_transfer_buckets?: string[];
    /**
     * Bucket keys whose `stock` includes Stock Transfer and which get a Stock Transfer column:
     * `internal` (Overall layout, channel pivot), `overall` + every warehouse (Internal pivot).
     */
    stock_transfer_buckets?: string[];
    aggregate_row?: Record<string, DrrBucket>;
    total: number;
    page: number;
    page_size: number;
    inventory_config: {
        inventory_period: 'DAY' | 'MONTH';
        under_stock_limit?: number;
        over_stock_limit?: number;
        drr_window: number;
        effective_days: number;
        /** The basis this response was computed on — a node pivot always reports BACKWARD. */
        basis?: RateBasis;
        /** The org's SAVED defaults, so the control can tell a preview from the committed value. */
        saved_basis?: RateBasis;
        sales_lookback_days?: number;
    };
};

export type DrrViewPayload = {
    categories?: string[];
    productGroups?: string[];
    tiers?: string[];
    productIds?: string[];
    isActive?: boolean;
    search?: string;
    page?: number;
    pageSize?: number;
    sortColumn?: 'sku' | 'drr' | 'doh';
    sortDirection?: 'ASC' | 'DESC';
    /** Window length in days — lookback on BACKWARD, forecast horizon on FORWARD. */
    drrWindow?: number;
    /**
     * Rate basis override. Omit to use the org's saved default. Honoured by the Overall
     * layout only — a node pivot always answers on past sales (see `basis` in the response).
     */
    basis?: RateBasis;
    /** Node mode: pivot columns to this channel's nodes (per FC / feeder / store). */
    channelKey?: string;
};

export const getDrrView = async (
    payload: DrrViewPayload = {},
): Promise<DrrViewResponse> => {
    const body = { ...payload };
    if (!body.search) {
        delete body.search;
    }
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/drr',
        body: JSON.stringify(body),
    });
};

/** One open transfer behind a Stock Transfer cell (same lines that produced the figure). */
export type StockTransferLine = {
    stnId: string;
    code: string;
    fromWarehouse: string | null;
    toWarehouse: string | null;
    status: string;
    /**
     * The source system's own status word when the line is mirrored from one (Amazon's
     * WORKING / IN_TRANSIT / RECEIVING, Uniware's RETURN_AWAITED). Null on platform STNs.
     */
    externalStatus?: string | null;
    /** Who moves a mirrored shipment (Amazon: USE_YOUR_OWN_CARRIER / AMAZON_PARTNERED_CARRIER). */
    transportMode?: string | null;
    /** ISO timestamp the source created / approved the note (a mirror: the source's creation time). */
    approvedAt?: string | null;
    /** yyyy-MM-dd the source declared the units would leave. */
    plannedDispatchDate?: string | null;
    /** ISO timestamp the units left the source. */
    dispatchedAt: string | null;
    /** yyyy-MM-dd the units are expected (or were booked) at the destination. */
    expectedDate?: string;
    /** True when `expectedDate` is the destination's own booked date, not a lane estimate. */
    expectedIsBooked?: boolean;
    /** ISO timestamp of the first receipt at the destination. */
    receivedAt?: string | null;
    dispatchedQty: number;
    receivedQty: number;
    /** Units still between the two warehouses — what the cell counts. */
    openQty: number;
};

export const getStockTransferLines = async (payload: {
    productId: string;
    /** Narrow to transfers into this internal warehouse (Internal pivot warehouse column). */
    warehouseName?: string;
}): Promise<{ data: StockTransferLine[]; total: number }> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/stock-transfer/lines',
        body: JSON.stringify(payload),
    });

/**
 * The two legs an In Transit reading is shown in when its source can tell them apart:
 * SCHEDULED = committed, not yet handed over (Amazon WORKING / READY_TO_SHIP);
 * IN_TRANSIT = left the dock, not yet booked in.
 */
export type ChannelShipmentLeg = 'SCHEDULED' | 'IN_TRANSIT';

/**
 * The open shipments behind one In Transit / Scheduled cell of a channel that reports its own
 * inbound (the Amazon FBA shipment mirror). Same line shape as a stock transfer: a shipment is
 * an STN whose destination is the channel's FC. Served by the same reader that sums the cell.
 */
export const getChannelShipmentLines = async (payload: {
    productId: string;
    /** The channel's abbreviation key, as the grids group columns (e.g. Amazon_FBA). */
    channelKey: string;
    /** Which cell was clicked. Omitted = both legs. */
    leg?: ChannelShipmentLeg;
    /** Narrow to shipments into this FC (a warehouse column in the channel pivot / Snapshot). */
    warehouseName?: string;
}): Promise<{ data: StockTransferLine[]; total: number }> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/channel-shipment/lines',
        body: JSON.stringify(payload),
    });

export type DrrNode = {
    warehouse_id: string;
    name: string;
    city?: string | null;
    /** Internal drill-in: on-hand plus `stock_transfer`. */
    stock: number;
    /** Internal drill-in only: units on a transfer to this warehouse, included in `stock`. */
    stock_transfer?: number | null;
    drr: number;
    doh: number | null;
};

export type DrrNodesResponse = {
    channel_key: string;
    channel_drr: number;
    channel_stock: number;
    nodes: DrrNode[];
    unattributed_drr: number;
    effective_days: number;
};

export const getDrrNodes = async (payload: {
    channelKey: string;
    productId?: string;
    drrWindow?: number;
}): Promise<DrrNodesResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/drr/nodes',
        body: JSON.stringify(payload),
    });
};

/** One batch of an item at a warehouse, in the merchant's picking order (FEFO or FIFO). '' = no-batch bucket. */
export type BatchCandidate = {
    batchId: string;
    batchCode: string | null;
    expiryDate: string | null;
    availableQuantity: number;
};

export const getBatchCandidates = (payload: {
    warehouseId: string;
    itemId: string;
    itemType?: 'product' | 'material';
}): Promise<BatchCandidate[]> =>
    ReparoApi<BatchCandidate[]>({
        method: 'POST',
        url: '/warehouse-stock/batch-candidates',
        body: JSON.stringify(payload),
    });

/** One warehouse's stock, one line per item (batches summed). */
export type SingleWarehouseStockItem = {
    product_id: string;
    product_name: string | null;
    sku: string | null;
    quantity: number;
    on_hold_stock?: number | null;
};

export const getSingleWarehouseStock = (
    warehouseId: string,
    payload: { itemType?: 'product' | 'material'; fetchAll?: boolean; search?: string },
): Promise<{ data: SingleWarehouseStockItem[]; total: number }> =>
    ReparoApi({
        method: 'POST',
        url: `/warehouse-stock/${encodeURIComponent(warehouseId)}`,
        body: JSON.stringify(payload),
    });

// ── SKU Ageing (inventory-ageing-v1) ─────────────────────────────────────────

export type InventoryAgeBuckets = {
    /** Units per age bucket; one more entry than `bucket_edges`, the last being "older than the last edge". */
    units: number[];
    /** Units no receipt in our history can account for — older than `receipts_from_date`, not fresh stock. */
    older_than_history: number;
    /** On-hand-weighted average age in days of the dated units; null when nothing could be dated. */
    weighted_age_days: number | null;
    oldest_receipt_date: string | null;
};

/**
 * A DoH View row plus its age. `internal` is the internal pool's stock / DRR / DoH exactly as
 * the DoH View computes it; `channels` is always empty — ageing has no channel pivot.
 */
export type InventoryAgeingRow = Omit<DrrViewRow, 'subRows'> & {
    age: InventoryAgeBuckets;
    /** WAREHOUSE scope: the warehouse this row's units stand in. */
    warehouse_id?: string;
    warehouse_name?: string;
    subRows?: InventoryAgeingRow[];
};

export type InventoryAgeingScope = 'INTERNAL' | 'WAREHOUSE';

export type InventoryAgeingResponse = {
    data: InventoryAgeingRow[];
    /** Totals over the full filtered set, not the page. */
    aggregate_row?: InventoryAgeingRow;
    bucket_edges: number[];
    as_of_date: string;
    receipts_from_date: string | null;
    scope: InventoryAgeingScope;
    total: number;
    page: number;
    page_size: number;
    inventory_config: DrrViewResponse['inventory_config'];
};

export type InventoryAgeingPayload = Omit<DrrViewPayload, 'sortColumn' | 'basis' | 'channelKey'> & {
    /** 'age' (on-hand-weighted, the default) | 'older_than_history' | 'doh' | 'drr' | 'sku'. */
    sortColumn?: 'age' | 'older_than_history' | 'doh' | 'drr' | 'sku';
    /** Override the age-bucket upper edges in days, ascending. Omit for the org's saved edges. */
    bucketEdges?: number[];
    /** 'INTERNAL' (default): one row per SKU, the internal pool. 'WAREHOUSE': one row per SKU per warehouse, stock only. */
    scope?: InventoryAgeingScope;
};

export const getInventoryAgeing = async (
    payload: InventoryAgeingPayload = {},
): Promise<InventoryAgeingResponse> => {
    const body = { ...payload };
    if (!body.search) {
        delete body.search;
    }
    return ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/ageing',
        body: JSON.stringify(body),
    });
};

export type InventoryAgeingReceipt = {
    grn_id: string | null;
    warehouse_id: string;
    warehouse_name: string;
    date: string;
    quantity: number;
    /** Units of today's on-hand attributed to this receipt; 0 = assumed sold. */
    allocated: number;
    age_days: number;
    bucket_index: number | null;
    /** WAREHOUSE drill only: the transfer that brought these units here; null when received in place. */
    via?: string | null;
    /** WAREHOUSE drill only: no receipt in our history explains these units. */
    untraced?: boolean;
};

export type InventoryAgeingReceiptsResponse = {
    product_id: string;
    on_hand: number;
    older_than_history: number;
    bucket_edges: number[];
    as_of_date: string;
    /** Newest first. */
    receipts: InventoryAgeingReceipt[];
};

export const getInventoryAgeingReceipts = async (payload: {
    productId: string;
    bucketEdges?: number[];
    /** Drill into one warehouse's row: the layers standing there after the chained replay. */
    warehouseId?: string;
}): Promise<InventoryAgeingReceiptsResponse> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/warehouse-stock/ageing/receipts',
        body: JSON.stringify(payload),
    });
